import { z } from 'zod'
import { AsyncFn, IsoDateString } from '../../common'
import { guidSchema } from '../../contracts/_common'
import {
  AppointmentType,
  EstimateGuid,
  ForCompany,
  InvoiceGuid,
  JobAppointmentGuid,
  JobGuid,
  LocationGuid,
  PaymentMethod,
  PaymentRecordGuid,
} from '..'
import { bzOptional } from '../common-schemas'

type LocationSummary = {
  locationGuid: LocationGuid
  addressLine1: string
}

type InvoiceSummary = {
  invoiceGuid: InvoiceGuid
  displayId: string
  totalUsc: number
}

type EstimateSummary = {
  estimateGuid: EstimateGuid
  displayId: number
  totalUsc: number
}

/*
  Summary of a payment linked to a job. Use the payment_links table
*/
type PaymentSummary = {
  paymentRecordGuid: PaymentRecordGuid
  amountUsd: number
  paymentMethod: PaymentMethod
}

type JobVisitSummary = {
  jobAppointmentGuid: JobAppointmentGuid
  appointmentWindowStart: IsoDateString
  appointmentType: AppointmentType
}

export type JobSummary = {
  jobLlmSummary: string
  jobGuid: JobGuid
  displayId: number
  jobTypeName: string
  createdAt: IsoDateString
  workCompletedAt?: IsoDateString
  locationSummary: LocationSummary
  invoiceSummaries: InvoiceSummary[]
  estimateSummaries: EstimateSummary[]
  paymentSummaries: PaymentSummary[]
  jobVisitSummaries: JobVisitSummary[]
}

export const serviceHistoryInputSchema = z
  .object({
    accountGuid: bzOptional(guidSchema),
    locationGuid: bzOptional(guidSchema),
    jobGuid: bzOptional(guidSchema),
    numMonthsAgo: bzOptional(z.number().int().positive()),
  })
  .refine(input => Boolean(input.accountGuid || input.locationGuid), 'An account or location is required')

export type ServiceHistoryInput = z.infer<typeof serviceHistoryInputSchema>
export const serviceHistoryAvailabilityInputSchema = z.object({
  scopes: z.array(serviceHistoryInputSchema).min(1).max(50),
})
export type ServiceHistoryAvailabilityInput = z.infer<typeof serviceHistoryAvailabilityInputSchema>
export type ServiceHistoryRequest = ForCompany<ServiceHistoryInput>

export type ServiceHistoryResponse = {
  serviceHistoryLlmSummary: string
  jobSummaries: JobSummary[]
}

export type ServiceHistoryReader = AsyncFn<ServiceHistoryRequest, ServiceHistoryResponse>
